import { Component, forwardRef, Input, ViewChild } from '@angular/core';
import {
  DateRange,
  MatDateRangeInput,
  MatStartDate,
  MatEndDate,
  MatCalendar,
} from '@angular/material/datepicker';
import {
  MatButtonToggleChange,
  MatButtonToggleGroup,
  MatButtonToggle,
} from '@angular/material/button-toggle';
import { MatMenuTrigger, MatMenu } from '@angular/material/menu';
import { ButtonSize, ButtonStyle } from '@constants/button/1.0';
import { ControlValueAccessor, UntypedFormGroup, NG_VALUE_ACCESSOR } from '@angular/forms';
import { DateService } from '@services/utilities/1.0/date.service';
import { MatFormField, MatLabel, MatSuffix, MatHint } from '@angular/material/form-field';
import { NgIf, NgFor, KeyValuePipe } from '@angular/common';
import { MatButton } from '@angular/material/button';
import { ButtonComponent } from '../button/1.0/button.component';
import { TranslateModule } from '@ngx-translate/core';

@Component({
  selector: 'txp-date-range-cva',
  templateUrl: './date-range-cva.component.html',
  styleUrls: ['./date-range-cva.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => DateRangeCvaComponent),
      multi: true,
    },
  ],
  standalone: true,
  imports: [
    MatFormField,
    MatMenuTrigger,
    MatLabel,
    MatDateRangeInput,
    MatStartDate,
    MatEndDate,
    NgIf,
    MatSuffix,
    MatButton,
    MatHint,
    MatMenu,
    MatButtonToggleGroup,
    NgFor,
    MatButtonToggle,
    MatCalendar,
    ButtonComponent,
    TranslateModule,
    KeyValuePipe,
  ],
})
export class DateRangeCvaComponent implements ControlValueAccessor {
  @ViewChild(MatMenuTrigger) dateRangeTrigger: MatMenuTrigger;
  @Input() source: string;
  @Input() set value(val: { start: string | Date; end: string | Date }) {
    if (val?.start && val?.end) {
      this.dateRange = new DateRange(
        this.dateService.toDate(val.start),
        this.dateService.toDate(val.end),
      );
    } else {
      this.dateRange = new DateRange(null, null);
    }
  }

  dateRangePresets = DateRangePresets;
  dateRangePresetsFuture = DateRangePresetsFuture;
  tempDateRangePreset?: DateRangePresets;

  dateRange = new DateRange<Date>(null, null);
  tempDateRange = new DateRange<Date>(null, null);
  buttonStyle = ButtonStyle; // expose enum to template
  buttonSize = ButtonSize; // expose enum to template
  onChangeFn: any;
  onTouchedFn: any;
  form: UntypedFormGroup;
  disabled: boolean;
  maxDate = new Date();
  minDate = null;

  constructor(private dateService: DateService) {}

  registerOnChange(fn: any): void {
    this.onChangeFn = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouchedFn = fn;
  }

  writeValue(obj: { start: string | Date; end: string | Date }): void {
    this.minDate = null;
    if (this.source === 'orders') {
      this.minDate = this.dateService.add(new Date(), -59, 'day');
    }

    if (this.source === 'scheduledOrders') {
      this.minDate = new Date();
    }

    if (obj?.start && obj?.end) {
      this.dateRange = new DateRange(
        this.dateService.toDate(obj.start),
        this.dateService.toDate(obj.end),
      );
    } else {
      this.clearDateRange();
    }

    this.onChange(obj);
  }

  onChange(val) {
    if (this.onChangeFn) {
      this.onChangeFn(val);
    }
  }

  setDateRange(event: MatButtonToggleChange) {
    if (this.source === 'orders') {
      this.setPresetDateRange(event);
    }
    if (this.source === 'scheduledOrders') {
      this.setPresetDateRangeFuture(event);
    }
    this.confirmDateRangeChange();
  }

  setPresetDateRange({ value }: MatButtonToggleChange) {
    let start = this.dateService.startOf(this.dateService.toDate(new Date()), 'day');
    let end = this.dateService.startOf(this.dateService.toDate(new Date()), 'day');
    switch (value) {
      case DateRangePresets.TODAY:
        break;
      case DateRangePresets.YESTERDAY:
        start = this.dateService.add(start, -1, 'day');
        end = this.dateService.add(end, -1, 'day');
        break;
      case DateRangePresets.WEEK_TO_DATE:
        start = this.dateService.startOf(start, 'week');
        break;
      case DateRangePresets.LAST7:
        start = this.dateService.add(start, -6, 'day');
        break;
      case DateRangePresets.LAST15:
        start = this.dateService.add(start, -14, 'day');
        break;
      case DateRangePresets.LAST30:
        start = this.dateService.add(start, -29, 'day');
        break;
      case DateRangePresets.LAST60:
        start = this.dateService.add(start, -59, 'day');
        break;
      case DateRangePresets.MONTH_TO_DATE:
        start = this.dateService.startOf(start, 'month');
        break;
      case DateRangePresets.LAST_MONTH:
        start = this.dateService.startOf(this.dateService.add(start, -1, 'month'), 'month');
        end = this.dateService.add(this.dateService.startOf(end, 'month'), -1, 'day');
        break;
    }
    this.tempDateRange = new DateRange(start, end);
  }

  setPresetDateRangeFuture({ value }: MatButtonToggleChange) {
    let start = this.dateService.startOf(this.dateService.toDate(new Date()), 'day');
    let end = this.dateService.startOf(this.dateService.toDate(new Date()), 'day');
    switch (value) {
      case DateRangePresets.TODAY:
        break;
      case DateRangePresetsFuture.TOMORROW:
        start = this.dateService.add(start, 1, 'day');
        end = this.dateService.add(end, 1, 'day');
        break;
      case DateRangePresetsFuture.NEXT7:
        end = this.dateService.add(end, 6, 'day');
        break;
      case DateRangePresetsFuture.NEXT15:
        end = this.dateService.add(end, 14, 'day');
        break;
      case DateRangePresetsFuture.NEXT30:
        end = this.dateService.add(end, 29, 'day');
        break;
    }
    this.tempDateRange = new DateRange(start, end);
  }

  setTempDateRange(event: Date) {
    const { start, end } = this.tempDateRange;
    const selected = event;

    if (!start || end) {
      this.tempDateRange = new DateRange(selected, null);
    } else if (this.dateService.isSameOrAfter(selected, start)) {
      this.tempDateRange = new DateRange(start, selected);
    } else {
      this.tempDateRange = new DateRange(selected, start);
    }

    this.tempDateRangePreset = undefined;
  }

  preservePresetOrder() {
    return 0;
  }

  cancelDateRangeChange() {
    this.clearTempDateRange();
    this.dateRangeTrigger.closeMenu();
  }

  clearDateRange() {
    this.dateRange = new DateRange(null, null);
    this.onChange(this.dateRange);
  }

  clearTempDateRange() {
    this.tempDateRange = new DateRange(null, null);
    this.tempDateRangePreset = undefined;
  }

  confirmDateRangeChange() {
    this.dateRange = this.tempDateRange;
    this.clearTempDateRange();
    this.dateRangeTrigger.closeMenu();
    this.onChange(this.dateRange);
  }

  convertToDate(value: string | Date): Date {
    if (typeof value === 'string') {
      const date = new Date(value);
      if (!isNaN(date?.getTime())) {
        return date;
      }
    }
    return value as Date;
  }
}

enum DateRangePresets {
  TODAY = 'today',
  YESTERDAY = 'yesterday',
  WEEK_TO_DATE = 'weekToDate',
  LAST7 = 'last7',
  LAST15 = 'last15',
  LAST30 = 'last30',
  LAST60 = 'last60',
  MONTH_TO_DATE = 'monthToDate',
  LAST_MONTH = 'lastMonth',
}

enum DateRangePresetsFuture {
  TODAY = 'today',
  TOMORROW = 'tomorrow',
  NEXT7 = 'next7',
  NEXT15 = 'next15',
  NEXT30 = 'next30',
}
